import { useCallback, useEffect, useRef, useState } from 'react'

import { PropertyListDetailAttributes } from './propertyListTableHelpers'
import {
  LABEL_DRUH_KONSTRUKCE,
  LABEL_KANALIZACE,
  LABEL_PLYN,
  LABEL_TYP_SO,
  LABEL_VODOVOD,
  LABEL_VYTAH,
  LABEL_VYTAPENI,
  LABEL_ZPUSOB_VYUZITI,
} from './propertyListTableHelpers'

interface ArcGisFeatureResponse<T = Record<string, unknown>> {
  features: { attributes: T }[]
}

export type TransformedAttributes = PropertyListDetailAttributes & {
  typstavebnihoobjektuText: string | null
  zpusobvyuzitiText: string | null
  druhkonstrukceText: string | null
  pripojenikanalizaceText: string | null
  pripojeniplynText: string | null
  pripojenivodovodText: string | null
  vybavenivytahemText: string | null
  zpusobvytapeniText: string | null
  castobceText: string | null
}

export interface PropertyListDetailRow extends TransformedAttributes {
  key: string
}

const LAYER_URL =
  'https://ags.cuzk.gov.cz/arcgis/rest/services/RUIAN/MapServer/3/query'
const MUNICIPAL_PARTS_LAYER_URL =
  'https://ags.cuzk.gov.cz/arcgis/rest/services/RUIAN/MapServer/11/query'

const OUT_FIELDS =
  'objectid,kod,nespravny,cisladomovni,identifikacniparcela,typstavebnihoobjektukod,zpusobvyuzitikod,castobce,momc,platiod,isknbudovaid,dokonceni,druhkonstrukcekod,obestavenyprostor,pocetbytu,pocetpodlazi,podlahovaplocha,pripojenikanalizacekod,pripojeniplynkod,pripojenivodovodkod,vybavenivytahemkod,zastavenaplocha,zpusobvytapenikod,zdroj,st_area(shape)'

const mapLabel = (value: number | null, labels: Record<number, string>) =>
  value != null ? labels[value] ?? null : null

const transformAttributes = (
  attrs: PropertyListDetailAttributes,
  municipalNames: Record<number, { name: string }> | undefined
): TransformedAttributes => ({
  ...attrs,
  typstavebnihoobjektuText: mapLabel(
    attrs.typstavebnihoobjektukod,
    LABEL_TYP_SO
  ),
  zpusobvyuzitiText: mapLabel(attrs.zpusobvyuzitikod, LABEL_ZPUSOB_VYUZITI),
  druhkonstrukceText: mapLabel(attrs.druhkonstrukcekod, LABEL_DRUH_KONSTRUKCE),
  pripojenikanalizaceText: mapLabel(
    attrs.pripojenikanalizacekod,
    LABEL_KANALIZACE
  ),
  pripojeniplynText: mapLabel(attrs.pripojeniplynkod, LABEL_PLYN),
  pripojenivodovodText: mapLabel(attrs.pripojenivodovodkod, LABEL_VODOVOD),
  vybavenivytahemText: mapLabel(attrs.vybavenivytahemkod, LABEL_VYTAH),
  zpusobvytapeniText: mapLabel(attrs.zpusobvytapenikod, LABEL_VYTAPENI),
  castobceText:
    attrs.castobce != null
      ? municipalNames?.[attrs.castobce]?.name || String(attrs.castobce)
      : null,
})

export const useFetchPropertyListBuildingDetail = (whereClause?: string) => {
  const [data, setData] = useState<PropertyListDetailRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const buildSearchParams = useCallback(() => {
    if (!whereClause) return null
    let parsedWhere: unknown
    try {
      parsedWhere = JSON.parse(whereClause)
    } catch {
      return null
    }
    const parsed = parsedWhere as { where?: string; geometry?: string }
    const params = new URLSearchParams({
      f: 'json',
      where: parsed?.where || '1=0',
      outFields: OUT_FIELDS,
      returnGeometry: 'false',
    })
    if (parsed?.geometry) {
      params.set('geometryType', 'esriGeometryPolygon')
      params.set('spatialRel', 'esriSpatialRelIntersects')
      params.set('geometry', parsed.geometry)
      params.set('inSR', '4326')
      params.set('outSR', '4326')
    }
    return params
  }, [whereClause])

  const fetchData = useCallback(async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setLoading(true)
    setError(null)

    const params = buildSearchParams()

    if (!whereClause || !params) {
      setData([])
      setLoading(false)
      return
    }

    try {
      const response = await fetch(LAYER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
        signal: controller.signal,
      })

      if (!response.ok) throw new Error('HTTP ' + response.status)

      const featureResponse: ArcGisFeatureResponse<PropertyListDetailAttributes> =
        await response.json()

      const municipalCodesSet = new Set<number>()

      for (const feature of featureResponse.features || []) {
        const municipalPartCode = feature.attributes.castobce as number | null
        if (typeof municipalPartCode === 'number') {
          municipalCodesSet.add(municipalPartCode)
        }
      }

      const municipalCodes = Array.from(municipalCodesSet)
      let municipalNames: Record<number, { name: string }> | undefined

      if (municipalCodes.length) {
        const nameParams = new URLSearchParams({
          f: 'json',
          where: `kod IN (${municipalCodes.join(',')})`,
          outFields: 'objectid,kod,nazev',
          returnGeometry: 'false',
        })

        const municipalNamesResponse = await fetch(MUNICIPAL_PARTS_LAYER_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: nameParams.toString(),
          signal: controller.signal,
        })

        if (municipalNamesResponse.ok) {
          const municipalNamesResponseJson: ArcGisFeatureResponse<{
            kod: number
            nazev: string
          }> = await municipalNamesResponse.json()
          municipalNames = (municipalNamesResponseJson.features || []).reduce(
            (acc, item) => {
              acc[item.attributes.kod] = { name: item.attributes.nazev }
              return acc
            },
            {} as Record<number, { name: string }>
          )
        }
      }

      const propertyRows: PropertyListDetailRow[] = (
        featureResponse.features || []
      ).map((feature, idx) => ({
        key: String(feature.attributes.objectid || idx + 1),
        ...transformAttributes(feature.attributes, municipalNames),
      }))
      setData(propertyRows)
    } catch (e: unknown) {
      const errorObj = e as { name?: string; message?: string }
      if (errorObj?.name !== 'AbortError')
        setError(errorObj.message || 'Chyba při načítání')
    } finally {
      setLoading(false)
    }
  }, [buildSearchParams, whereClause])

  useEffect(() => {
    fetchData()
    return () => abortRef.current?.abort()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}
